"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { Button, Modal, ModalHeader, ModalBody, FormGroup, Label, Input, Spinner, FormFeedback } from "reactstrap"
import { VehicleVisitService } from "../../../Service/TicketingService"
import Swal from "sweetalert2"

interface Props {
  isOpen: boolean
  toggle: () => void
  onSuccess: () => void
  verificationData: any
}

const VehiclePaymentModal: React.FC<Props> = ({ isOpen, toggle, onSuccess, verificationData }) => {
  const [form, setForm] = useState({
    operator: "MTN",
    phone_number: "",
    tx_reference: "",
  })
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<{ [key: string]: string }>({})

  useEffect(() => {
    if (verificationData && isOpen) {
      // Reset form when modal opens with new data
      setForm({
        operator: "MTN",
        phone_number: "",
        tx_reference: "",
      })
      setErrors({})
    }
  }, [verificationData, isOpen])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setForm({
      ...form,
      [name]: value,
    })

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }))
    }
  }

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {}

    if (!form.phone_number.trim()) {
      newErrors.phone_number = "Phone number is required"
    }

    if (!form.tx_reference.trim()) {
      newErrors.tx_reference = "Transaction reference is required"
    }

    return newErrors
  }

  const handleSubmit = async () => {
    const validationErrors = validateForm()
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      return
    }

    try {
      setLoading(true)
      const response = await VehicleVisitService.updatePayment({
        visit_id: verificationData.visit_id,
        plate: verificationData.plate,
        amount: verificationData.amount,
        operator: form.operator,
        phone_number: form.phone_number,
        tx_reference: form.tx_reference,
      })

      if (response.success) {
        await Swal.fire({
          title: "Payment Recorded!",
          text: response.message,
          icon: "success",
          html: `
            <div class="text-start">
              <p><strong>Plate:</strong> ${response.visit_info.plate}</p>
              <p><strong>Amount:</strong> FRW ${response.visit_info.amount.toLocaleString()}</p>
              <p><strong>TX Reference:</strong> ${response.visit_info.tx_reference}</p>
              <p><strong>Status:</strong> ${response.visit_info.status}</p>
            </div>
          `,
        })
        onSuccess()
      }
    } catch (error: any) {
      console.error("Failed to record payment:", error)
      await Swal.fire("Error", error.response?.data?.message || "Failed to record payment", "error")
    } finally {
      setLoading(false)
    }
  }

  if (!verificationData) return null

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="md">
      <ModalHeader toggle={toggle}>Process Payment</ModalHeader>
      <ModalBody>
        <div className="mb-3 p-3 bg-primary rounded">
          <h6>Payment Summary</h6>
          <p className="mb-1">
            <strong>Plate:</strong> {verificationData.plate}
          </p>
          <p className="mb-1">
            <strong>Duration:</strong> {verificationData.duration_minutes} minutes
          </p>
          <p className="mb-0">
            <strong>Amount:</strong> FRW {verificationData.amount.toLocaleString()}
          </p>
        </div>

        <FormGroup>
          <Label for="operator">Payment Operator *</Label>
          <Input type="select" name="operator" value={form.operator} onChange={handleChange}>
            <option value="MTN">MTN </option>
           
            <option value="AIRTELMONEY">AIRTEL MONEY</option>
            
          </Input>
        </FormGroup>

        <FormGroup>
          <Label for="phone_number">Phone Number *</Label>
          <Input
            name="phone_number"
            value={form.phone_number}
            onChange={handleChange}
            invalid={!!errors.phone_number}
            placeholder="Enter phone number"
          />
          <FormFeedback>{errors.phone_number}</FormFeedback>
        </FormGroup>

        <FormGroup>
          <Label for="tx_reference">Transaction Reference *</Label>
          <Input
            name="tx_reference"
            value={form.tx_reference}
            onChange={handleChange}
            invalid={!!errors.tx_reference}
            placeholder="Enter transaction reference"
          />
          <FormFeedback>{errors.tx_reference}</FormFeedback>
        </FormGroup>

        <div className="text-end mt-4">
          <Button color="secondary" onClick={toggle} className="me-2">
            Cancel
          </Button>
          <Button color="success" onClick={handleSubmit} disabled={loading}>
            {loading ? <Spinner size="sm" /> : "Record Payment"}
          </Button>
        </div>
      </ModalBody>
    </Modal>
  )
}

export default VehiclePaymentModal
